/**
 * CompanyProfileTab Component
 *
 * Company profile with inline editing.
 * Displays relationship info, company info, contact info (emails, phones, addresses).
 */

import { useTranslation } from 'react-i18next';
import { companiesApi, COMPANY_RELATION_TYPES } from '@/services/contactsApi';
import type { Company } from '../types';
import EditableField from '@/components/tasks/EditableField';
import { EmailsSection, PhonesSection, AddressesSection } from '../sections';

interface CompanyProfileTabProps {
  company: Company;
  onUpdate: () => void;
  onError: (message: string) => void;
}

export default function CompanyProfileTab({
  company,
  onUpdate,
  onError,
}: CompanyProfileTabProps) {
  const { t } = useTranslation('people');
  const handleFieldUpdate = async (field: string, value: string | null) => {
    try {
      await companiesApi.update(company.id, { [field]: value });
      onUpdate();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to update field');
    }
  };

  return (
    <div className="space-y-6">
      {/* Company Info */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-sm font-medium text-gray-500 uppercase mb-4">{t('company_profile_tab.sections.company_info')}</h3>
        <div className="grid gap-4 md:grid-cols-2">
          {/* Domain */}
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase mb-1">{t('company_profile_tab.fields.domain')}</label>
            <EditableField
              type="text"
              value={company.domain || ''}
              onSave={(v) => handleFieldUpdate('domain', v || null)}
              placeholder={t('company_profile_tab.placeholders.domain')}
              emptyText={t('company_profile_tab.empty_text.domain')}
            />
          </div>

          {/* Industry */}
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase mb-1">{t('company_profile_tab.fields.industry')}</label>
            <EditableField
              type="text"
              value={company.industry || ''}
              onSave={(v) => handleFieldUpdate('industry', v || null)}
              placeholder={t('company_profile_tab.placeholders.industry')}
              emptyText={t('company_profile_tab.empty_text.industry')}
            />
          </div>

          {/* Size */}
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase mb-1">{t('company_profile_tab.fields.company_size')}</label>
            <EditableField
              type="text"
              value={company.size || ''}
              onSave={(v) => handleFieldUpdate('size', v || null)}
              placeholder={t('company_profile_tab.placeholders.company_size')}
              emptyText={t('company_profile_tab.empty_text.company_size')}
            />
          </div>

          {/* Website */}
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase mb-1">{t('company_profile_tab.fields.website')}</label>
            <EditableField
              type="text"
              value={company.website || ''}
              onSave={(v) => handleFieldUpdate('website', v || null)}
              placeholder={t('company_profile_tab.placeholders.website')}
              emptyText={t('company_profile_tab.empty_text.website')}
            />
          </div>
        </div>

        {/* Description */}
        <div className="mt-6">
          <label className="block text-xs font-medium text-gray-500 uppercase mb-1">{t('company_profile_tab.fields.description')}</label>
          <EditableField
            type="textarea"
            value={company.description || ''}
            onSave={(v) => handleFieldUpdate('description', v || null)}
            placeholder={t('company_profile_tab.placeholders.description')}
            emptyText={t('company_profile_tab.empty_text.description')}
            rows={4}
          />
        </div>

        {/* Tags */}
        {company.tags && company.tags.length > 0 && (
          <div className="mt-6">
            <label className="block text-xs font-medium text-gray-500 uppercase mb-2">{t('company_profile_tab.fields.tags')}</label>
            <div className="flex flex-wrap gap-2">
              {company.tags.map((tag, i) => (
                <span key={i} className="px-2 py-1 bg-gray-100 text-gray-700 text-sm rounded">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Social links */}
        <div className="mt-6">
          <label className="block text-xs font-medium text-gray-500 uppercase mb-2">{t('company_profile_tab.fields.linkedin')}</label>
          <EditableField
            type="text"
            value={company.linkedin_url || ''}
            onSave={(v) => handleFieldUpdate('linkedin_url', v || null)}
            placeholder={t('company_profile_tab.placeholders.linkedin')}
            emptyText={t('company_profile_tab.empty_text.linkedin')}
          />
          {company.linkedin_url && (
            <div className="mt-2">
              <a
                href={company.linkedin_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                </svg>
                {t('company_profile_tab.view_on_linkedin')}
              </a>
            </div>
          )}
        </div>
      </div>

      {/* Relationship Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-sm font-medium text-gray-500 uppercase mb-4">{t('company_profile_tab.sections.relationship')}</h3>
        <div className="space-y-4">
          {/* Short Summary */}
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase mb-1">{t('company_profile_tab.fields.summary')}</label>
            <div className="relative">
              <EditableField
                type="textarea"
                value={company.short_bio || ''}
                onSave={(v) => handleFieldUpdate('short_bio', v || null)}
                placeholder={t('company_profile_tab.placeholders.summary')}
                emptyText={t('company_profile_tab.empty_text.summary')}
                rows={2}
              />
              <div className="text-xs text-gray-400 text-right mt-1">
                {(company.short_bio || '').length}/200
              </div>
            </div>
          </div>
          {/* Relationship Goal */}
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase mb-1">{t('company_profile_tab.fields.our_goal')}</label>
            <div className="relative">
              <EditableField
                type="textarea"
                value={company.relationship_goal || ''}
                onSave={(v) => handleFieldUpdate('relationship_goal', v || null)}
                placeholder={t('company_profile_tab.placeholders.our_goal')}
                emptyText={t('company_profile_tab.empty_text.our_goal')}
                rows={2}
              />
              <div className="text-xs text-gray-400 text-right mt-1">
                {(company.relationship_goal || '').length}/200
              </div>
            </div>
          </div>
          {/* Relation Type */}
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase mb-1">{t('company_profile_tab.fields.relation_type')}</label>
            <EditableField
              type="select"
              value={company.relationship_to_user || 'unknown'}
              onSave={(v) => handleFieldUpdate('relationship_to_user', v)}
              options={COMPANY_RELATION_TYPES.map(r => ({ value: r.value, label: r.label }))}
            />
          </div>
        </div>
      </div>

      {/* Contact Info Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-sm font-medium text-gray-500 uppercase mb-4">{t('company_profile_tab.sections.contact_info')}</h3>
        <div className="space-y-6">
          <EmailsSection
            emails={company.emails || []}
            entityId={company.id}
            entityType="company"
            onUpdate={onUpdate}
            onError={onError}
          />
          <PhonesSection
            phones={company.phones || []}
            entityId={company.id}
            entityType="company"
            onUpdate={onUpdate}
            onError={onError}
          />
          <AddressesSection
            addresses={company.addresses || []}
            companyId={company.id}
            onUpdate={onUpdate}
            onError={onError}
          />
        </div>
      </div>
    </div>
  );
}
